import React, { useEffect, useRef, useState } from 'react';
import {
  Tooltip,
  Box,
  Card,
  CardHeader,
  Container,
  Divider,
  Grid,
  IconButton,
  TableContainer,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableFooter,
  TablePagination,
  Button,
} from '@mui/material';
import { Formik } from 'formik';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CustomerDialog from '../Components/AddPetDialog';
import axios from 'axios';
import { toast } from 'react-toastify';
import SearchIcon from '@mui/icons-material/Search';
import { useTheme } from '@mui/material/styles';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import LastPageIcon from '@mui/icons-material/LastPage';
import AnimalHistory from '../Components/AnimalHistory';
import Barcode from 'react-barcode';
import { useReactToPrint } from 'react-to-print';

function TablePaginationActions(props) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
}

function PetManagement() {
  const [pageTitle, setPageTitle] = useState('Pet Management');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [animalData, setAnimalData] = useState([]);
  const [search, setSearch] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const [updateID, setUpdateID] = useState(0);
  const [barcodes, setBarcodes] = useState('');
  const [petUpdateID, setPetUpdateID] = useState(0);

  const componentRef = useRef();

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: 'Barcodes',
    onAfterPrint: () => {
      setBarcodes('');
    },
  });

  const printBarcode = (barcodeValue) => {
    setBarcodes(barcodeValue);
  };

  useEffect(() => {
    if (barcodes) {
      handlePrint();
    }
  }, [barcodes]);

  useEffect(() => {
    getAllAnimalDetails();
  }, []);

  useEffect(() => {
    if (!dialogOpen) {
      getAllAnimalDetails();
    }
  }, [dialogOpen]);

  const getAllAnimalDetails = () => {
    axios.get('https://serverfourpaws.ccsoftwares.com/Pet/getAllPetDetails').then((res) => {
      if (res.data.length > 0) {
        setAnimalData(res.data);
      } else {
        setAnimalData([]);
        toast.error('No records to display!');
      }
    });
  };

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - animalData.length) : 0;

  const updateRowData = (id) => {
    setUpdateID(id);
    setDialogOpen(true);
  };

  const handleClick = () => {
    setDialogOpen(true);
  };

  const historyClick = (id) => {
    setUpdateID(id);
    setHistoryOpen(true);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  function EditPetDetails(AnimalID) {
    setPetUpdateID(AnimalID);
    setDialogOpen(true);
  }

  const cardTitle = (titleName) => {
    return (
      <Grid container spacing={1}>
        <Grid item md={11} xs={11}>
          {titleName}
        </Grid>
        <Grid item md={1} xs={1}>
          <Box display="flex" justifyContent="flex-end" alignItems="center">
            <Tooltip title="Add Pet">
              <IconButton onClick={handleClick}>
                <AddCircleOutlineIcon sx={{ color: 'black' }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Grid>
      </Grid>
    );
  };

  return (
    <>
      <Container maxWidth={false} sx={{ paddingTop: '15vh' }}>
        <Formik enableReinitialize>
          {({
            errors,
            handleBlur,
            handleSubmit,
            touched,
            values,
            setFieldValue,
          }) => (
            <form>
              <Box mt={0}>
                <Box sx={{ display: 'flex', justifyContent: 'end' }}>
                  <SearchIcon sx={{ color: 'action.active', mr: 1, my: 1 }} />
                  <TextField
                    id="input-with-sx"
                    variant="standard"
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                    }}

                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                      }
                    }}
                    color="success"
                  />
                </Box>
                <Card sx={{ padding: '15px' }}>
                  <CardHeader title={cardTitle(pageTitle)} />
                  <Divider />
                  <br />
                  {animalData.length > 0 ? (
                    <TableContainer>
                      <br />
                      <Table
                        sx={{ minWidth: 500 }}
                        size="small"
                        aria-label="a dense table"
                      >
                        <TableHead>
                          <TableRow>
                            <TableCell align="left" sx={{ fontWeight: 'bold' }}>
                              #
                            </TableCell>
                            <TableCell align="left" sx={{ fontWeight: 'bold' }}>
                              Pet Name
                            </TableCell>
                            <TableCell align="left" sx={{ fontWeight: 'bold' }}>
                              Pet Type
                            </TableCell>
                            <TableCell align="left" sx={{ fontWeight: 'bold' }}>
                              Owner Name
                            </TableCell>
                            <TableCell align="left" sx={{ fontWeight: 'bold' }}>
                              Owner Phone
                            </TableCell>
                            <TableCell align="left" sx={{ fontWeight: 'bold' }}>
                              Pet Code
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                              Actions
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(rowsPerPage > 0
                            ? animalData.slice(
                              page * rowsPerPage,
                              page * rowsPerPage + rowsPerPage
                            )
                            : animalData
                          )
                            .filter((element) => {
                              if (search === '') {
                                return element;
                              } else if (
                                element.petCode.toString().toLowerCase().includes(search.toLowerCase())
                              ) {
                                return element;
                              }
                              return null;
                            })
                            .map((row, i) => (
                              <TableRow key={row._id} sx={{ alignContent: 'left' }}>
                                <TableCell align="left">{i + 1}</TableCell>
                                <TableCell align="left">{row.AnimalName}</TableCell>
                                <TableCell align="left">{row.AnimalType}</TableCell>
                                <TableCell align="left">{row.OwnerName}</TableCell>
                                <TableCell align="left">{row.OwnerPhone}</TableCell>
                                <TableCell align="left">{row.petCode}</TableCell>
                                <TableCell align="center">
                                  <Button
                                    variant="contained"
                                    onClick={() => historyClick(row.AnimalID)}
                                    size="small"
                                  >
                                    History
                                  </Button>
                                  &nbsp; &nbsp; &nbsp;
                                  <Button
                                    variant="contained"
                                    onClick={() => printBarcode(row.petCode)}
                                    size="small"
                                  >
                                    Barcode
                                  </Button>
                                  &nbsp; &nbsp; &nbsp;
                                  <Button
                                    variant="contained"
                                    onClick={() => EditPetDetails(row.AnimalID)}
                                    size="small"
                                  >
                                    Edit
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          {emptyRows > 0 && (
                            <TableRow style={{ height: 53 * emptyRows }}>
                              <TableCell colSpan={7} />
                            </TableRow>
                          )}
                        </TableBody>
                        <TableFooter>
                          <TableRow>
                            <TablePagination
                              rowsPerPageOptions={[
                                10,
                                25,
                                35,
                                { label: 'All', value: -1 },
                              ]}
                              colSpan={7}
                              count={animalData.length}
                              rowsPerPage={rowsPerPage}
                              page={page}
                              SelectProps={{
                                inputProps: {
                                  'aria-label': 'rows per page',
                                },
                                native: true,
                              }}
                              onPageChange={handleChangePage}
                              onRowsPerPageChange={handleChangeRowsPerPage}
                              ActionsComponent={TablePaginationActions}
                            />
                          </TableRow>
                        </TableFooter>
                      </Table>
                    </TableContainer>
                  ) : null}
                </Card>
              </Box>
            </form>
          )}
        </Formik>
      </Container>

      {/* Add Pet Dialog */}
      <CustomerDialog
        dialogOpen={dialogOpen}
        setDialogOpen={setDialogOpen}
        petUpdateID={petUpdateID}
        setPetUpdateID={setPetUpdateID}
      />

      {/* Animal History Dialog */}
      <AnimalHistory
        dialogOpen={historyOpen}
        setDialogOpen={setHistoryOpen}
        updateID={updateID}
        setUpdateID={setUpdateID}
      />

      {/* Barcode Printing Component */}
      {barcodes && (
        <div
          style={{
            position: 'absolute',
            top: '-1000px',
            left: '-1000px',
          }}
        >
          <BarcodePrint ref={componentRef} barcodeValue={barcodes} />
        </div>
      )}
    </>
  );
}

// BarcodePrint Component
const BarcodePrint = React.forwardRef(({ barcodeValue }, ref) => (
  <div ref={ref}>
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <div
        className="barcode-container"
        style={{
          display: 'flex', justifyContent: 'space-around', marginTop: '10px',
          marginLeft: '0px',
        }}
      >
        <div>
          <Barcode
            value={barcodeValue.toString()}
            width={1.2}
            height={45}
            format="CODE128"
            displayValue={true}
            fontSize={10}
            margin={0}
            renderer="svg"
          />
          <p
            style={{
              marginTop: '0px',
              fontSize: '8px',
              textAlign: 'center',
              fontWeight: 'bold'
            }}
          >
            {barcodeValue} - 4 Paws
          </p>



        </div>
      </div>
    </div>
  </div>
));

export default PetManagement;
